// Copyright (c) 2023-present VexFlow contributors: https://github.com/vexflow/vexflow/graphs/contributors
// MIT License
// @author Mohit Cheppudira
import { BoundingBox } from './boundingbox';
import { Font } from './font';
import { Metrics } from './metrics';
import { Registry } from './registry';
import { Category } from './typeguard';
import { defined, prefix, RuntimeError } from './util';
/**
 * Element implements a generic base class for VexFlow, with implementations
 * of general functions and properties that can be inherited by all VexFlow elements.
 *
 * The Element handles style and font properties for the Element and any child
 * elements, along with working with the Registry to create unique ids.
 *
 * The `text` is a series of unicode characters (including SMuFL codes).
 * The `textFont` property contains information required to style the text (i.e., font family, size, weight, and style).
 * This font family is a comma separated list of fonts.
 * The method `measureText` calculates the `textMetrics`, `boundingBox`, `height` and `width` of the `text`.
 * The method `renderText(...)` will render the text using the provided context and coordinates,
 * taking `xShift` and `yShift` into account.
 */
export class Element {
    static get CATEGORY() {
        return Category.Element;
    }
    static newID() {
        return `auto${Element.ID++}`;
    }
    // Note: Canvas is node-canvas.
    // https://www.npmjs.com/package/canvas
    static setTextMeasurementCanvas(canvas /* | Canvas */) {
        Element.txtCanvas = canvas;
    }
    static getTextMeasurementCanvas() {
        let txtCanvas = Element.txtCanvas;
        // Create the canvas element that will be used to measure text.
        if (!txtCanvas) {
            if (typeof document !== 'undefined') {
                txtCanvas = document.createElement('canvas'); // Defaults to 300 x 150. See: https://www.w3.org/TR/2012/WD-html5-author-20120329/the-canvas-element.html#the-canvas-element
            }
            else if (typeof OffscreenCanvas !== 'undefined') {
                txtCanvas = new OffscreenCanvas(300, 150);
            }
            Element.txtCanvas = txtCanvas;
        }
        return txtCanvas;
    }
    constructor(category) {
        var _a;
        this.children = [];
        this.style = {};
        this._text = '';
        this.metricsValid = false;
        this._textMetrics = {
            fontBoundingBoxAscent: 0,
            fontBoundingBoxDescent: 0,
            actualBoundingBoxAscent: 0,
            actualBoundingBoxDescent: 0,
            actualBoundingBoxLeft: 0,
            actualBoundingBoxRight: 0,
            width: 0,
            alphabeticBaseline: 0,
            emHeightAscent: 0,
            emHeightDescent: 0,
            hangingBaseline: 0,
            ideographicBaseline: 0,
        };
        this._height = 0;
        this._width = 0;
        this.xShift = 0;
        this.yShift = 0;
        this.x = 0;
        this.y = 0;
        this.attrs = {
            id: Element.newID(),
            type: category !== null && category !== void 0 ? category : this.constructor.CATEGORY,
            class: '',
        };
        this.rendered = false;
        this._fontInfo = Metrics.getFontInfo(this.attrs.type);
        this.style = Metrics.getStyle(this.attrs.type);
        this.fontScale = Metrics.get(`${this.attrs.type}.fontScale`);
        // If a default registry exist, then register with it right away.
        (_a = Registry.getDefaultRegistry()) === null || _a === void 0 ? void 0 : _a.register(this);
    }
    /**
     * Adds a child to the Element, which lets it inherit the
     * same style as the parent when setGroupStyle() is called.
     *
     * Examples of children are noteheads and stems.  Modifiers such
     * as Accidentals are generally not set as children.
     *
     * Note that StaveNote calls setGroupStyle() when setStyle() is called.
     */
    addChild(child) {
        if (child.parent)
            throw new RuntimeError('Element', 'Parent already defined');
        child.parent = this;
        this.children.push(child);
        return this;
    }
    getCategory() {
        return this.attrs.type;
    }
    /**
     * Set the element style used to render.
     *
     * Example:
     * ```typescript
     * element.setStyle({ fillStyle: 'red', strokeStyle: 'red' });
     * element.drawWithStyle();
     * ```
     * Note: If the element draws additional sub-elements (i.e.: Modifiers in a Stave),
     * the style can be applied to all of them by means of the context:
     * ```typescript
     * element.setStyle({ fillStyle: 'red', strokeStyle: 'red' });
     * element.getContext().setFillStyle('red');
     * element.getContext().setStrokeStyle('red');
     * element.drawWithStyle();
     * ```
     * or using drawWithStyle:
     * ```typescript
     * element.setStyle({ fillStyle: 'red', strokeStyle: 'red' });
     * element.drawWithStyle();
     * ```
     */
    setStyle(style) {
        this.style = style;
        return this;
    }
    /** Set the element & associated children style used for rendering. */
    setGroupStyle(style) {
        this.style = style;
        this.children.forEach((child) => child.setGroupStyle(style));
        return this;
    }
    /** Get the element style used for rendering. */
    getStyle() {
        return this.style;
    }
    /** Apply the element style to `context`. */
    applyStyle(context = this.context, style = this.getStyle()) {
        if (!context)
            return this;
        if (style.shadowColor)
            context.setShadowColor(style.shadowColor);
        if (style.shadowBlur)
            context.setShadowBlur(style.shadowBlur);
        if (style.fillStyle)
            context.setFillStyle(style.fillStyle);
        if (style.strokeStyle)
            context.setStrokeStyle(style.strokeStyle);
        if (style.lineWidth)
            context.setLineWidth(style.lineWidth);
        if (style.lineDash)
            context.setLineDash(style.lineDash.split(' ').map(Number));
        return this;
    }
    /**
     * Draw the element and all its sub-elements (i.e.: Modifiers in a Stave)
     * with the element's style (see `getStyle()` and `setStyle()`)
     */
    drawWithStyle() {
        const ctx = this.checkContext();
        ctx.save();
        this.applyStyle(ctx);
        this.draw();
        ctx.restore();
        return this;
    }
    /** Draw an element. */
    draw() {
        throw new RuntimeError('Element', 'Draw not defined');
    }
    /** Check if it has a class label (An element can have multiple class labels). */
    hasClass(className) {
        var _a;
        if (!this.attrs.class)
            return false;
        return ((_a = this.attrs.class) === null || _a === void 0 ? void 0 : _a.split(' ').indexOf(className)) !== -1;
    }
    /** Add a class label (An element can have multiple class labels). */
    addClass(className) {
        var _a;
        if (this.hasClass(className))
            return this;
        if (!this.attrs.class)
            this.attrs.class = `${className}`;
        else
            this.attrs.class = `${this.attrs.class} ${className}`;
        (_a = this.registry) === null || _a === void 0 ? void 0 : _a.onUpdate({
            id: this.attrs.id,
            name: 'class',
            value: className,
            oldValue: undefined,
        });
        return this;
    }
    /** Remove a class label (An element can have multiple class labels). */
    removeClass(className) {
        var _a, _b;
        if (!this.hasClass(className))
            return this;
        const arr = (_a = this.attrs.class) === null || _a === void 0 ? void 0 : _a.split(' ');
        if (arr) {
            arr.splice(arr.indexOf(className));
            this.attrs.class = arr.join(' ');
        }
        (_b = this.registry) === null || _b === void 0 ? void 0 : _b.onUpdate({
            id: this.attrs.id,
            name: 'class',
            value: undefined,
            oldValue: className,
        });
        return this;
    }
    /** Call back from registry after the element is registered. */
    onRegister(registry) {
        this.registry = registry;
        return this;
    }
    /** Return the rendered status. */
    isRendered() {
        return this.rendered;
    }
    /** Set the rendered status. */
    setRendered(rendered = true) {
        this.rendered = rendered;
        return this;
    }
    /** Return the element attributes. */
    getAttributes() {
        return this.attrs;
    }
    /** Return an attribute, such as 'id', 'type' or 'class'. */
    // eslint-disable-next-line
    getAttribute(name) {
        return this.attrs[name];
    }
    /** Return associated SVGElement. */
    getSVGElement(suffix = '') {
        const id = prefix(this.attrs.id + suffix);
        const element = document.getElementById(id);
        if (element)
            return element;
    }
    /** Set an attribute such as 'id', 'class', or 'type'. */
    setAttribute(name, value) {
        var _a;
        const oldID = this.attrs.id;
        const oldValue = this.attrs[name];
        this.attrs[name] = value;
        // Register with old id to support id changes.
        (_a = this.registry) === null || _a === void 0 ? void 0 : _a.onUpdate({ id: oldID, name, value, oldValue });
        return this;
    }
    /** Get the boundingBox. */
    getBoundingBox() {
        return new BoundingBox(this.x + this.xShift, this.y + this.yShift - this.textMetrics.actualBoundingBoxAscent, this.width, this.height);
    }
    /** Return the context, such as an SVGContext or CanvasContext object. */
    getContext() {
        return this.context;
    }
    /** Set the context to an SVGContext or CanvasContext object */
    setContext(context) {
        this.context = context;
        return this;
    }
    /** Validate and return the rendering context. */
    checkContext() {
        return defined(this.context, 'NoContext', 'No rendering context attached to instance.');
    }
    //////////////////////////////////////////////////////////////////////////////////////////////////
    // Font Handling
    /**
     * Provide a CSS compatible font string (e.g., 'bold 16px Arial') that will be applied
     * to text (not glyphs).
     */
    set font(f) {
        this.setFont(f);
    }
    /** Returns the CSS compatible font string for the text font. */
    get font() {
        return Font.toCSSString(this._fontInfo);
    }
    /**
     * Set the element's text font family, size, weight, style
     * (e.g., `Arial`, `10pt`, `bold`, `italic`).
     *
     * This attribute does not determine the font used for musical Glyphs like treble clefs.
     *
     * @param font is 1) a `FontInfo` object or
     *                2) a string formatted as CSS font shorthand (e.g., 'bold 10pt Arial') or
     *                3) a string representing the font family (at least one of `size`, `weight`, or `style` must also be provided).
     * @param size a string specifying the font size and unit (e.g., '16pt'), or a number (the unit is assumed to be 'pt').
     * @param weight is a string (e.g., 'bold', 'normal') or a number (100, 200, ... 900).
     * @param style is a string (e.g., 'italic', 'normal').
     * If no arguments are provided, then the font is set to the default font.
     * Each Element subclass may specify its own default by overriding the static `TEXT_FONT` property.
     */
    setFont(font, size, weight, style) {
        const defaultTextFont = Metrics.getFontInfo(this.attrs.type);
        const fontIsObject = typeof font === 'object';
        const fontIsString = typeof font === 'string';
        const sizeWeightStyleAreUndefined = size === undefined && weight === undefined && style === undefined;
        this.metricsValid = false;
        if (fontIsObject) {
            // `font` is case 1) a FontInfo object
            this._fontInfo = Object.assign(Object.assign({}, defaultTextFont), font);
        }
        else if (fontIsString && sizeWeightStyleAreUndefined) {
            // `font` is case 2) CSS font shorthand.
            this._fontInfo = Font.fromCSSString(font);
        }
        else {
            // `font` is case 3) a font family string (e.g., 'Times New Roman').
            // The other parameters represent the size, weight, and style.
            // It is okay for `font` to be undefined while one or more of the other arguments is provided.
            // Following CSS conventions, unspecified params are reset to the default.
            this._fontInfo = Font.validate(font !== null && font !== void 0 ? font : defaultTextFont.family, size !== null && size !== void 0 ? size : defaultTextFont.size, weight !== null && weight !== void 0 ? weight : defaultTextFont.weight, style !== null && style !== void 0 ? style : defaultTextFont.style);
        }
        return this;
    }
    /**
     * Get the css string describing this Element's text font. e.g.,
     * 'bold 10pt Arial'.
     */
    getFont() {
        return Font.toCSSString(this._fontInfo);
    }
    /** Return a copy of the current FontInfo object. */
    get fontInfo() {
        // We can cast to Required<FontInfo> here, because
        // we just called resetFont() above to ensure this.fontInfo is set.
        return this._fontInfo;
    }
    /** Set the current FontInfo object. */
    set fontInfo(fontInfo) {
        this.setFont(fontInfo);
    }
    /** Change the font size, while keeping everything else the same. */
    setFontSize(size) {
        const fontInfo = this.fontInfo;
        this.setFont(fontInfo.family, size, fontInfo.weight, fontInfo.style);
        return this;
    }
    /**
     * @returns a CSS font-size string (e.g., '18pt', '12px', '1em').
     * See Element.fontSizeInPixels or Element.fontSizeInPoints if you need to get a number for calculation purposes.
     */
    getFontSize() {
        return this.fontSize;
    }
    getFontScale() {
        return this.fontScale;
    }
    /**
     * The size is 1) a string of the form '10pt' or '16px', compatible with the CSS font-size property.
     *          or 2) a number, which is interpreted as a point size (i.e. 12 == '12pt').
     */
    set fontSize(size) {
        this.setFontSize(size);
    }
    /** @returns a CSS font-size string (e.g., '18pt', '12px', '1em'). */
    get fontSize() {
        let size = this.fontInfo.size;
        if (typeof size === 'number') {
            size = `${size}pt`;
        }
        return size;
    }
    /** @returns the font size in `pt`. */
    get fontSizeInPoints() {
        return Font.convertSizeToPointValue(this.fontSize);
    }
    /** @returns the font size in `px`. */
    get fontSizeInPixels() {
        return Font.convertSizeToPixelValue(this.fontSize);
    }
    /** @returns a CSS font-style string (e.g., 'italic'). */
    get fontStyle() {
        return this.fontInfo.style;
    }
    /** Set the font style. */
    set fontStyle(style) {
        const fontInfo = this.fontInfo;
        this.setFont(fontInfo.family, fontInfo.size, fontInfo.weight, style);
    }
    /**
     * @returns a CSS font-weight string (e.g., 'bold').
     * As in CSS, font-weight is always returned as a string, even if it was set as a number.
     */
    get fontWeight() {
        return this.fontInfo.weight + '';
    }
    /** Set the font weight. */
    set fontWeight(weight) {
        const fontInfo = this.fontInfo;
        this.setFont(fontInfo.family, fontInfo.size, weight, fontInfo.style);
    }
    /** Get element width. */
    getWidth() {
        return this.width;
    }
    get width() {
        if (!this.metricsValid)
            this.measureText();
        return this._width;
    }
    /** Set element width. */
    setWidth(width) {
        this.width = width;
        return this;
    }
    set width(width) {
        if (!this.metricsValid)
            this.measureText();
        this._width = width;
    }
    /** Set the X coordinate. */
    setX(x) {
        this.x = x;
        return this;
    }
    /** Get the X coordinate. */
    getX() {
        return this.x;
    }
    /** Get the Y coordinate. */
    getY() {
        return this.y;
    }
    /** Set the Y coordinate. */
    setY(y) {
        this.y = y;
        return this;
    }
    /** Shift element down `yShift` pixels. Negative values shift up. */
    setYShift(yShift) {
        this.yShift = yShift;
        return this;
    }
    /** Get shift element `yShift`. */
    getYShift() {
        return this.yShift;
    }
    /** Set shift element right `xShift` pixels. Negative values shift left. */
    setXShift(xShift) {
        this.xShift = xShift;
        return this;
    }
    /** Get shift element `xShift`. */
    getXShift() {
        return this.xShift;
    }
    /** Set element text. */
    setText(text) {
        this.text = text;
        return this;
    }
    set text(text) {
        this.metricsValid = false;
        this._text = text;
    }
    /** Get element text. */
    getText() {
        return this._text;
    }
    get text() {
        return this._text;
    }
    /** Render the element text. */
    renderText(ctx, xPos, yPos) {
        ctx.setFont(this._fontInfo);
        ctx.fillText(this._text, xPos + this.x + this.xShift, yPos + this.y + this.yShift);
        this.children.forEach((child) => {
            // changed -- do not look at private attributes of children.
            ctx.setFont(child.fontInfo);
            ctx.fillText(child.text, xPos + child.x + child.xShift, yPos + child.y + child.yShift);
        });
    }
    /** Measure the text using the textFont. */
    measureText() {
        var _a;
        // TODO: What about SVG.getBBox()?
        // https://developer.mozilla.org/en-US/docs/Web/API/SVGGraphicsElement/getBBox
        const context = (_a = Element.getTextMeasurementCanvas()) === null || _a === void 0 ? void 0 : _a.getContext('2d');
        if (!context) {
            // eslint-disable-next-line no-console
            console.warn('Element: No context for txtCanvas. Returning empty text metrics.');
            return this._textMetrics;
        }
        context.font = Font.toCSSString(Font.validate(this.fontInfo));
        this._textMetrics = context.measureText(this.text);
        this._height = this._textMetrics.actualBoundingBoxAscent + this._textMetrics.actualBoundingBoxDescent;
        this._width = this._textMetrics.width;
        this.metricsValid = true;
        return this._textMetrics;
    }
    /** Measure the text using the FontInfo related with key. */
    static measureWidth(text, key = '') {
        var _a;
        const context = (_a = Element.getTextMeasurementCanvas()) === null || _a === void 0 ? void 0 : _a.getContext('2d');
        if (!context) {
            // eslint-disable-next-line no-console
            console.warn('Element: No context for txtCanvas. Returning empty text metrics.');
            return 0;
        }
        context.font = Font.toCSSString(Metrics.getFontInfo(key));
        return context.measureText(text).width;
    }
    /** Get the text metrics. */
    getTextMetrics() {
        return this.textMetrics;
    }
    get textMetrics() {
        if (!this.metricsValid)
            this.measureText();
        return this._textMetrics;
    }
    /** Get the text height. */
    getHeight() {
        return this.height;
    }
    get height() {
        if (!this.metricsValid)
            this.measureText();
        return this._height;
    }
    set height(height) {
        if (!this.metricsValid)
            this.measureText();
        this._height = height;
    }
    setOriginX(x) {
        const bbox = this.getBoundingBox();
        const originX = Math.abs((bbox.getX() - this.xShift) / bbox.getW());
        const xShift = (x - originX) * bbox.getW();
        this.xShift = -xShift;
    }
    setOriginY(y) {
        const bbox = this.getBoundingBox();
        const originY = Math.abs((bbox.getY() - this.yShift) / bbox.getH());
        const yShift = (y - originY) * bbox.getH();
        this.yShift = -yShift;
    }
    setOrigin(x, y) {
        this.setOriginX(x);
        this.setOriginY(y);
    }
}
Element.ID = 1000;
